import React, { useState, useEffect } from 'react'
import { Plus, X, Building2, AlertCircle, Trash2 } from 'lucide-react'

interface NdaCompanyBypassProps {
  tenantId: string
}

const NdaCompanyBypass: React.FC<NdaCompanyBypassProps> = ({ tenantId }) => {
  const [bypasses, setBypasses] = useState<string[]>([])
  const [newBypass, setNewBypass] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBypasses = async () => {
    try {
      const response = await fetch(`/api/nda/bypasses?tenantId=${tenantId}`)
      if (!response.ok) {
        throw new Error('Failed to fetch bypasses')
      }
      const data = await response.json()
      setBypasses(data.bypasses || [])
    } catch (err) {
      console.error('Failed to fetch NDA bypasses:', err)
      setError('Failed to load bypasses')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBypasses()
  }, [tenantId])

  const addBypass = async () => {
    if (!newBypass.trim()) return

    try {
      const response = await fetch('/api/nda/bypasses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          action: 'add',
          company: newBypass.trim()
        })
      })

      if (!response.ok) {
        throw new Error('Failed to add bypass')
      }

      setBypasses([...bypasses, newBypass.trim()])
      setNewBypass('')
      setError(null)
    } catch (err) {
      setError('Failed to add bypass')
    }
  }

  const removeBypass = async (company: string) => {
    try {
      const response = await fetch('/api/nda/bypasses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          action: 'remove',
          company
        })
      })

      if (!response.ok) {
        throw new Error('Failed to remove bypass')
      }

      setBypasses(bypasses.filter(b => b !== company))
      setError(null)
    } catch (err) {
      setError('Failed to remove bypass')
    }
  }

  if (loading) {
    return <div className="text-gray-500">Loading bypasses...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">
          Bypass NDA requirements for specific users or companies. Add individual email addresses (e.g., "user@acme.com"),
          email domains (e.g., "acme.com"), or company names to exempt them from NDA acceptance.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Add new bypass */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newBypass}
          onChange={(e) => setNewBypass(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addBypass()}
          placeholder="Enter email, domain, or company name (e.g., user@acme.com or acme.com)"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <button
          onClick={addBypass}
          disabled={!newBypass.trim()}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      {/* List of bypasses */}
      {bypasses.length > 0 ? (
        <div className="space-y-2">
          {bypasses.map((company) => (
            <div
              key={company}
              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-900">{company}</span>
              </div>
              <button
                onClick={() => removeBypass(company)}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Remove bypass"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 text-sm">
          No bypasses configured. Add users, domains, or companies to exempt them from NDA requirements.
        </div>
      )}
    </div>
  )
}

export default NdaCompanyBypass
